import type { ModelAssetNode } from '../assets/model-asset-types'
import {
  resolveMountedProjectModelNodeRuntimeAssemblyNodeId,
} from './project-model-node-identity'

/** 当前项目一级 occurrence 为新加载子树保留的挂载槽位。 */
export interface FirstLevelOccurrenceMount {
  /** 当前 occurrence 的稳定运行时装配身份。 */
  readonly runtimeAssemblyNodeId: string
  /** 当前 occurrence 的稳定结构 key。 */
  readonly nodeKey: string
  /** 当前 occurrence 的父节点结构 key。 */
  readonly parentNodeKey: string
  /** 一级 occurrence 的固定项目深度。 */
  readonly depth: 1
  /** 当前 occurrence 相对父装配的局部变换。 */
  readonly localTransform: readonly number[]
}

/**
 * 递归克隆后代并补齐运行时 occurrence 身份。
 *
 * @param node 新加载子树中的当前后代。
 * @param mountAssemblyNodeId 当前一级 occurrence 的运行时挂载身份。
 * @returns 保持结构顺序且身份完整的新后代子树。
 */
function mountLoadedDescendantOccurrenceIdentities(
  node: ModelAssetNode,
  mountAssemblyNodeId: string,
): ModelAssetNode {
  const runtimeAssemblyNodeId = resolveMountedProjectModelNodeRuntimeAssemblyNodeId(
    node,
    mountAssemblyNodeId,
  )

  return {
    ...node,
    assemblyNodeId: runtimeAssemblyNodeId,
    children: node.children.map((child) => (
      mountLoadedDescendantOccurrenceIdentities(child, mountAssemblyNodeId)
    )),
  }
}

/**
 * 把新加载子树挂入当前一级 occurrence 槽位。
 *
 * 根节点继承旧槽位的结构、位置与运行身份；包内 source 身份及其全部资产描述保持不变。
 * 输入树始终只读，返回值为完整的新树。
 *
 * @param loadedRoot GetCadNodeTree 或新包映射得到的子树根。
 * @param mount 当前项目一级 occurrence 的权威挂载槽位。
 * @returns 已完成运行身份挂载的新子树。
 */
export function mountLoadedSubtreeOccurrenceIdentities(
  loadedRoot: ModelAssetNode,
  mount: FirstLevelOccurrenceMount,
): ModelAssetNode {
  return {
    ...loadedRoot,
    assemblyNodeId: mount.runtimeAssemblyNodeId,
    nodeKey: mount.nodeKey,
    parentNodeKey: mount.parentNodeKey,
    depth: mount.depth,
    localTransform: [...mount.localTransform],
    children: loadedRoot.children.map((child) => (
      mountLoadedDescendantOccurrenceIdentities(child, mount.runtimeAssemblyNodeId)
    )),
  }
}
